import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import axios from 'axios';

import { sym } from '../utils/currency';
const API = process.env.REACT_APP_API_URL;

const TYPE_COLOR = {
  'Acquisition Cost': 'primary',
  'Depreciation':     'error',
  'Write-Down':       'warning',
  'Appreciation':     'success',
  'Disposal':         'default'
};

const FALedgerEntries = () => {
  const navigate = useNavigate();
  const { faNo } = useParams();
  const [entries, setEntries] = useState([]);
  const [asset, setAsset] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    const load = async () => {
      try {
        const [ledgerRes, assetRes] = await Promise.all([
          axios.get(`${API}/api/fixed-assets/${faNo}/ledger`, { headers }),
          axios.get(`${API}/api/fixed-assets/${faNo}`, { headers })
        ]);
        setEntries(ledgerRes.data);
        setAsset(assetRes.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load FA ledger');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [faNo]); // eslint-disable-line

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading...</Typography>
    </Box>
  );

  const bookValue = entries.length ? entries[entries.length - 1].bookValueAfterPosting : 0;

  return (
    <Box sx={{ maxWidth: 1300, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography variant="h5" color="primary">FA Ledger Entries</Typography>
              {asset && (
                <Typography variant="body2" color="text.secondary">
                  {asset.faNo} — {asset.faName} | Posting Group: {asset.faPostingGroup}
                </Typography>
              )}
            </Box>
            <IconButton onClick={() => navigate(-1)} title="Back">
              <ArrowBack />
            </IconButton>
          </Box>

          {/* Summary cards */}
          {asset && (
            <Box display="flex" gap={3} mb={3} flexWrap="wrap">
              <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 2, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary">Acquisition Cost</Typography>
                <Typography variant="h6" fontWeight={700}>
                  {(asset.acquisitionCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'error.50', borderRadius: 2, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary">Accum. Depreciation</Typography>
                <Typography variant="h6" fontWeight={700} color="error.main">
                  ({(asset.accumulatedDepreciation || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })})
                </Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 2, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary">Book Value</Typography>
                <Typography variant="h6" fontWeight={700} color="success.dark">
                  {bookValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>
          )}

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {entries.length === 0 ? (
            <Typography color="text.secondary">No ledger entries yet. Post a Purchase Order with this FA to record an acquisition.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Entry No.</strong></TableCell>
                  <TableCell><strong>Posting Date</strong></TableCell>
                  <TableCell><strong>Posting Type</strong></TableCell>
                  <TableCell><strong>Document No.</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell align="right"><strong>Amount ({sym()})</strong></TableCell>
                  <TableCell align="right"><strong>Book Value After</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map(e => (
                  <TableRow key={e.entryNo} hover>
                    <TableCell><Typography variant="caption">{e.entryNo}</Typography></TableCell>
                    <TableCell>{e.faPostingDate ? new Date(e.faPostingDate).toLocaleDateString() : ''}</TableCell>
                    <TableCell>
                      <Chip label={e.faPostingType} size="small" variant="outlined"
                        color={TYPE_COLOR[e.faPostingType] || 'default'} />
                    </TableCell>
                    <TableCell>{e.documentNo}</TableCell>
                    <TableCell><Typography variant="caption">{e.description}</Typography></TableCell>
                    <TableCell align="right" sx={{ color: e.amount < 0 ? 'error.main' : 'success.dark', fontWeight: 600 }}>
                      {e.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {e.bookValueAfterPosting.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default FALedgerEntries;
